import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, DollarSign, TrendingUp, Lock, Loader } from 'lucide-react';
import { formatCurrency, parseAccountBalance } from '../utils/helpers';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API_BASE = 'https://tradebot-k4nt.onrender.com/api';

// Mirrors handelSubmitCryptoPeneExchange + handelClcikComfarmPene in index.js
// Buy PENE:  POST /api/setSwap        { username, amount: "$X" }
// Sell PENE: POST /api/setSwapPene    { username, amount: "$X" }
// On success: navigate to /investment-dashboard (mirrors window.location.replace("./investment.html"))

const PeneExchange = () => {
  const navigate = useNavigate();
  const { user: contextUser, fetchUserPublic } = useUser();
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState('buy');  // 'buy' | 'sell'
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');
  const [peneBalance, setPeneBalance] = useState(0);
  const [usdBalance, setUsdBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);

  // Mirrors setWapIvestment object from index.js
  // { cryptotype: 'buy'|'sell', amount: '$X', url: 'setSwap'|'setSwapPene' }
  const [swapInfo, setSwapInfo] = useState(null);

  useEffect(() => {
    if (!contextUser?.telegramName) { navigate('/login'); return; }
    setUser(contextUser);
    setUsdBalance(parseAccountBalance(contextUser.accountBalance));
    // Fetch live data
    fetchUserPublic(contextUser.telegramName).then(info => {
      if (info?.accountBalance) setUsdBalance(parseAccountBalance(info.accountBalance));
      if (info?.investmentBalance) setPeneBalance(parseFloat(info.investmentBalance) || 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextUser?.telegramName, navigate]);

  // Mirrors handelSubmitCryptoPeneExchange() in index.js
  // Builds swapInfo (equivalent to setWapIvestment), shows PIN modal
  const handleSubmitExchange = (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter a valid amount'); return; }

    if (mode === 'buy') {
      if (parseFloat(amount) > usdBalance) { toast.error('Insufficient USD balance'); return; }
      // Mirrors: document.getElementById("pin-confarm").innerText = `Buy ${getInfor} PENE for $${getInfor}`
      setSwapInfo({ cryptotype: 'buy', amount: `$${amount}`, url: 'setSwap', label: `Buy ${amount} PENE for $${amount}` });
    } else {
      if (parseFloat(amount) > peneBalance) { toast.error('Insufficient PENE balance'); return; }
      // Mirrors: document.getElementById("pin-confarm").innerText = `Sell ${getInforSell} PENE for $${getInforSell}`
      setSwapInfo({ cryptotype: 'sell', amount: `$${amount}`, url: 'setSwapPene', label: `Sell ${amount} PENE for $${amount}` });
    }

    setShowPinModal(true);
  };

  // Mirrors handelClcikComfarmPene() in index.js exactly
  // POST to /api/${setWapIvestment.url} with { username, amount }
  // On 200: navigate to investment dashboard (mirrors window.location.replace("./investment.html"))
  const handleConfirmExchange = async (e) => {
    e.preventDefault();
    if (!pin || pin.length < 4) { toast.error('Please enter your PIN'); return; }
    if (!swapInfo) return;

    setLoading(true);
    try {
      const configBody = {
        username: user.telegramName,
        amount: swapInfo.amount,  // "$X" format as in index.js
        pin: pin
      };

      const response = await fetch(`${API_BASE}/${swapInfo.url}`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(configBody)
      });

      const data = await response.json();

      if (response.status === 200) {
        toast.success(`✅ ${swapInfo.label.replace('Sell', 'Sold').replace('Buy', 'Bought')} successfully!`);
        setShowPinModal(false);
        setAmount('');
        setPin('');
        setSwapInfo(null);
        // Mirrors: window.location.replace("./investment.html")
        setTimeout(() => navigate('/investment-dashboard'), 1200);
      } else {
        // Mirrors: handelErrorView(data.error)
        toast.error(data?.error || 'Transaction failed. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowPinModal(false);
    setPin('');
    setSwapInfo(null);
  };

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-4">
            <ArrowLeftRight className="text-white" size={28} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">PENE Exchange</h1>
          <p className="text-slate-500">Buy or Sell PENE — 1 PENE = $1</p>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm text-green-700 mb-1 font-medium">USD Balance</p>
            <p className="text-2xl font-bold text-green-900">{formatCurrency(usdBalance)}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
            <p className="text-sm text-purple-700 mb-1 font-medium">PENE Balance</p>
            <p className="text-2xl font-bold text-purple-900">{peneBalance.toLocaleString()}</p>
          </div>
        </div>

        {/* Exchange Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">

          {/* Buy / Sell Toggle — mirrors handelClickViewBuySell in index.js */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={() => { setMode('buy'); setAmount(''); }}
              className={`flex-1 py-3 rounded-xl font-bold transition-all ${
                mode === 'buy'
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Buy PENE
            </button>
            <button
              onClick={() => { setMode('sell'); setAmount(''); }}
              className={`flex-1 py-3 rounded-xl font-bold transition-all ${
                mode === 'sell'
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Sell PENE
            </button>
          </div>

          {/* Rate display — mirrors: "Buy 1 PENE for $1" / "Sell 1 PENE for $1" */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 text-center">
            <p className="text-slate-600 font-medium text-sm">
              {mode === 'buy' ? '🟢 Buy 1 PENE for $1' : '🔴 Sell 1 PENE for $1'}
            </p>
            {/* mirrors: acc-pene element */}
            <p className="text-xs text-slate-400 mt-1">
              {mode === 'buy'
                ? `Account Balance: ${formatCurrency(usdBalance)}`
                : `Balance: ${peneBalance.toLocaleString()} PENE`}
            </p>
          </div>

          {/* Amount form — mirrors exchange-buy / exchange-sell sections in index.js */}
          <form onSubmit={handleSubmitExchange} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Amount to {mode === 'buy' ? 'spend (USD)' : 'sell (PENE)'}
              </label>
              <div className="relative">
                {mode === 'buy' ? (
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                ) : (
                  <TrendingUp className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                )}
                {/* mirrors: id="crypto-type-exchang" / id="crypto-type-exchang-sell" */}
                <input
                  id={mode === 'buy' ? 'crypto-type-exchang' : 'crypto-type-exchang-sell'}
                  type="number"
                  step="0.01"
                  placeholder="100"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-lg font-semibold"
                  required
                />
              </div>
            </div>

            {amount && parseFloat(amount) > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex justify-between items-center">
                <span className="text-slate-600 text-sm">You will receive</span>
                <span className="font-bold text-purple-700 text-lg">
                  {parseFloat(amount).toLocaleString()} {mode === 'buy' ? 'PENE' : 'USD'}
                </span>
              </div>
            )}

            <button
              type="submit"
              className={`w-full py-4 rounded-xl font-bold text-white shadow-md transition-all ${
                mode === 'buy'
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                  : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
              }`}
            >
              {mode === 'buy' ? '🛒 Buy PENE' : '💰 Sell PENE'}
            </button>
          </form>
        </div>

        {/* PIN Confirmation Modal — mirrors pin-bod section in original HTML */}
        {showPinModal && swapInfo && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl">

              <h2 className="text-xl font-bold text-slate-900 mb-2 text-center">Confirm Exchange</h2>

              {/* Mirrors: pin-confarm element from index.js */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 text-center">
                <p className="text-slate-500 text-xs mb-1">Transaction Summary</p>
                <p className="font-bold text-slate-900 text-lg">{swapInfo.label}</p>
              </div>

              <form onSubmit={handleConfirmExchange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Enter PIN</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    {/* mirrors: id="pin" input in the pin-bod form */}
                    <input
                      id="pin"
                      type="password"
                      placeholder="Your PIN"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="w-full pl-10 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-center text-xl tracking-[0.5em]"
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="py-3 bg-slate-900 text-white font-semibold rounded-xl hover:bg-slate-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? <><Loader size={16} className="animate-spin" /> Processing...</> : 'Confirm'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

export default PeneExchange;
